import { computed, type ComputedRef, type Ref } from 'vue'

import type { ApprovalStage, WorkflowInstance } from '@/types/workflow'
import {
  buildReviewSections,
  getApprovalStageLabel,
  getHistoricalMaxStage,
  getLatestReviewableStage,
  getPreferredApprovalStage,
  getReviewPrimaryContent,
} from '@/utils/workflowPresentation'

import { resolveLegacyAudioMixPreviewVideo, resolvePreferredFinalVideoSource } from './finalAudioMixDraft'
import type { StoryboardSceneDraft, StoryboardWorkspaceState, UploadedWorkspaceVideo } from './types'

function parseConfirmedStoryboardText(rawValue: unknown): StoryboardSceneDraft[] {
  const text = String(rawValue || '').trim()
  if (!text) {
    return []
  }

  try {
    const parsed = JSON.parse(text)
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item): item is Record<string, any> => Boolean(item) && typeof item === 'object' && !Array.isArray(item))
        .map((scene, index) => ({
          ...scene,
          scene_number: Number(scene.scene_number || index + 1),
        })) as StoryboardSceneDraft[]
    }
  } catch {
    // Fall through to the legacy text parser.
  }

  return text
    .split(/\n(?=场景\s*\d+[:：])/g)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, index) => {
      const lines = block
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)

      const heading = lines[0] || ''
      const headingMatch = heading.match(/^场景\s*(\d+)[:：](.+)$/)
      const sceneNumber = Number(headingMatch?.[1] || index + 1)
      const location = String(headingMatch?.[2] || `场景 ${index + 1}`).trim()

      const fieldMap = lines.slice(1).reduce<Record<string, string>>((result, line) => {
        const fieldMatch = line.match(/^(角色|对白重点|视觉线索|情绪氛围|背景提示|建议时长)[:：](.+)$/)
        if (fieldMatch) {
          result[fieldMatch[1]] = String(fieldMatch[2] || '').trim()
        }
        return result
      }, {})

      const durationMatch = String(fieldMap['建议时长'] || '').match(/(\d+(?:\.\d+)?)/)
      const visualCues = String(fieldMap['视觉线索'] || '')
        .split(/[、，,；;]/)
        .map((item) => item.trim())
        .filter(Boolean)

      const characters = String(fieldMap['角色'] || '')
        .split(/[、，,／/]/)
        .map((item) => item.trim())
        .filter(Boolean)

      return {
        scene_number: sceneNumber,
        location,
        description: fieldMap['视觉线索'] || location,
        action: fieldMap['背景提示'] || fieldMap['视觉线索'] || location,
        dialogue_summary: fieldMap['对白重点'] || '',
        mood: fieldMap['情绪氛围'] || '',
        background_prompt: fieldMap['背景提示'] || '',
        visual_cues: visualCues,
        characters,
        duration: durationMatch ? Number(durationMatch[1]) : undefined,
      }
    })
}

interface UseGenerationWorkspaceCoreStateOptions {
  storyboardWorkspaceInputKey: string
  routeStageValue: () => unknown
  workflow: Ref<WorkflowInstance | null>
  acting: Ref<boolean>
  generatedSceneVideoOverrides: Ref<Record<number, Record<string, any>>>
}

export function useGenerationWorkspaceCoreState(options: UseGenerationWorkspaceCoreStateOptions) {
  const stageOrder: ApprovalStage[] = ['proposal', 'script', 'storyboard', 'final']

  const requestedStage = computed<ApprovalStage | null>(() => {
    const value = String(options.routeStageValue() || '')
    if (['proposal', 'script', 'storyboard', 'final'].includes(value)) {
      return value as ApprovalStage
    }
    return null
  })

  const currentStage = computed<ApprovalStage>(() => {
    const pendingStage = options.workflow.value?.pending_approval?.stage
    const historicalMaxStage = getHistoricalMaxStage(options.workflow.value)
    const historicalMaxIndex = stageOrder.indexOf(historicalMaxStage)

    if (requestedStage.value) {
      const requestedIndex = stageOrder.indexOf(requestedStage.value)
      const pendingIndex = stageOrder.indexOf(pendingStage)

      if (requestedIndex !== -1 && requestedIndex <= historicalMaxIndex) {
        return requestedStage.value
      }

      if (pendingIndex !== -1) {
        return pendingStage as ApprovalStage
      }

      return historicalMaxStage
    }

    return (requestedStage.value || pendingStage || getLatestReviewableStage(options.workflow.value) || getPreferredApprovalStage(options.workflow.value)) as ApprovalStage
  })

  const displayStage = computed<ApprovalStage>(() => currentStage.value)
  const currentStageLabel = computed(() => getApprovalStageLabel(currentStage.value))
  const displayStageLabel = computed(() => getApprovalStageLabel(displayStage.value))
  const reviewSections = computed(() => (options.acting.value ? [] : buildReviewSections(options.workflow.value, currentStage.value)))
  const primaryStageContent = computed(() => getReviewPrimaryContent(reviewSections.value))

  const finalStageVideoUrl = computed(() => {
    const data = options.workflow.value?.data || {}
    return String(
      data.video_synthesis_result?.final_video_url
        || data.video_synthesis_result?.video?.final_video_url
        || data.final_packaging_result?.deliverables?.video_url
        || data.video?.file_url
        || ''
    ).trim()
  })

  const storyboardWorkspaceState = computed<StoryboardWorkspaceState>(() => {
    const state = options.workflow.value?.data?.input_data?.[options.storyboardWorkspaceInputKey]
    return state && typeof state === 'object' ? state as StoryboardWorkspaceState : {}
  })

  const storyboardSceneImageMap = computed<Record<number, Record<string, any>>>(() => {
    const raw = storyboardWorkspaceState.value.scene_images || {}
    return Object.entries(raw).reduce<Record<number, Record<string, any>>>((result, [key, value]) => {
      const sceneNumber = Number(key)
      if (sceneNumber && value && typeof value === 'object') {
        result[sceneNumber] = value as Record<string, any>
      }
      return result
    }, {})
  })

  const storyboardScenes = computed(() => {
    const scenes = options.workflow.value?.data?.storyboard_generation_result?.scenes
    const baseScenes = Array.isArray(scenes) && scenes.length
      ? scenes.map((scene, index) => ({
        ...scene,
        scene_number: Number(scene?.scene_number || index + 1),
      })) as StoryboardSceneDraft[]
      : parseConfirmedStoryboardText(options.workflow.value?.data?.input_data?.stage_confirmed_content?.storyboard)

    return baseScenes.map((scene, index) => {
      const sceneNumber = Number(scene?.scene_number || index + 1)
      const persistedImageState = storyboardSceneImageMap.value[sceneNumber]
      if (!persistedImageState) {
        return {
          ...scene,
          scene_number: sceneNumber,
        } as StoryboardSceneDraft
      }

      const persistedBackgroundImageUrl = String(persistedImageState.background_image_url || '').trim()
      const persistedSelectedImageSource = String(persistedImageState.selected_image_source || '').trim()
      const persistedBackgroundCleared = Boolean(persistedImageState.background_image_cleared)

      return {
        ...scene,
        scene_number: sceneNumber,
        background_image_url: persistedBackgroundCleared && !persistedBackgroundImageUrl
          ? ''
          : (persistedBackgroundImageUrl || String(scene?.background_image_url || '').trim()),
        selected_image_source: persistedBackgroundCleared && !persistedSelectedImageSource
          ? ''
          : (persistedSelectedImageSource || String(scene?.selected_image_source || '').trim()),
        background_image_cleared: persistedBackgroundCleared,
        reference_image_urls: Array.isArray(persistedImageState.reference_image_urls) && persistedImageState.reference_image_urls.length
          ? persistedImageState.reference_image_urls
          : scene?.reference_image_urls,
        character_images_urls: Array.isArray(persistedImageState.character_images_urls) && persistedImageState.character_images_urls.length
          ? persistedImageState.character_images_urls
          : scene?.character_images_urls,
        material_id: persistedImageState.material_id ?? scene?.material_id,
        background_generation: persistedImageState.background_generation || scene?.background_generation,
      } as StoryboardSceneDraft
    })
  })

  const sceneVideoTaskMap = computed<Record<number, Record<string, any>>>(() => {
    const raw = options.workflow.value?.data?.video_synthesis_result?.scene_tasks || options.workflow.value?.data?.video_synthesis_result?.video?.scene_tasks
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return {}
    }

    return Object.entries(raw).reduce<Record<number, Record<string, any>>>((result, [key, value]) => {
      const sceneNumber = Number(key)
      if (sceneNumber && value && typeof value === 'object') {
        result[sceneNumber] = value as Record<string, any>
      }
      return result
    }, {})
  })

  const uploadedSceneVideoMap = computed<Record<number, UploadedWorkspaceVideo>>(() => {
    const raw = storyboardWorkspaceState.value.scene_uploaded_videos || {}
    const mapped: Record<number, UploadedWorkspaceVideo> = {}
    Object.entries(raw).forEach(([key, value]) => {
      const sceneNumber = Number(key)
      if (sceneNumber && value?.url) {
        mapped[sceneNumber] = value
      }
    })
    return mapped
  })

  const generatedSceneVideoMap = computed<Record<number, Record<string, any>>>(() => {
    const scenes = options.workflow.value?.data?.video_synthesis_result?.scenes || options.workflow.value?.data?.video_synthesis_result?.video?.scenes
    const persisted = Array.isArray(scenes)
      ? scenes.reduce<Record<number, Record<string, any>>>((result, item, index) => {
        const sceneNumber = Number(item?.scene_number || index + 1)
        if (sceneNumber) {
          result[sceneNumber] = item as Record<string, any>
        }
        return result
      }, {})
      : {}

    return {
      ...persisted,
      ...options.generatedSceneVideoOverrides.value,
    }
  })

  const uploadedFinalVideo = computed<UploadedWorkspaceVideo | null>(() => storyboardWorkspaceState.value.final_delivery?.uploaded_final_video || null)
  const audioMixPreviewVideo = computed<UploadedWorkspaceVideo | null>(() => resolveLegacyAudioMixPreviewVideo(storyboardWorkspaceState.value.final_delivery))
  const audioMixDeliveryVideo = computed<UploadedWorkspaceVideo | null>(() => storyboardWorkspaceState.value.final_delivery?.audio_mix_delivery_video || null)
  const preferredFinalVideoSource = computed(() => resolvePreferredFinalVideoSource(storyboardWorkspaceState.value.final_delivery))
  const effectiveFinalVideoUrl = computed(() => {
    if (preferredFinalVideoSource.value === 'audio_mix_delivery' && audioMixDeliveryVideo.value?.url) {
      return audioMixDeliveryVideo.value.url
    }
    if (preferredFinalVideoSource.value === 'uploaded_final' && uploadedFinalVideo.value?.url) {
      return uploadedFinalVideo.value.url
    }
    if (preferredFinalVideoSource.value === 'audio_mix_delivery' && uploadedFinalVideo.value?.url) {
      return uploadedFinalVideo.value.url
    }
    if (preferredFinalVideoSource.value === 'uploaded_final' && audioMixDeliveryVideo.value?.url) {
      return audioMixDeliveryVideo.value.url
    }
    return finalStageVideoUrl.value
  })

  return {
    currentStage,
    currentStageLabel,
    displayStage,
    displayStageLabel,
    audioMixDeliveryVideo,
    audioMixPreviewVideo,
    effectiveFinalVideoUrl,
    finalStageVideoUrl,
    generatedSceneVideoMap,
    primaryStageContent,
    preferredFinalVideoSource,
    requestedStage,
    reviewSections,
    sceneVideoTaskMap,
    storyboardScenes,
    storyboardWorkspaceState,
    uploadedFinalVideo,
    uploadedSceneVideoMap,
  }
}